import { CountryDto, CustomerImpactDto } from 'generated/request-backend';
import {
    CUSTOMER_IMPACT_TABS,
    IImpactDescriptionFields,
    IMPORTANCE_REASON_LABELS,
    ImportanceReason,
    ICustomerImpactData,
    ISelectedCustomerImpact,
    OTHER_OPTION_KEY,
} from './types';
/**
 * Builds a human-readable impact description from the wizard's per-customer
 * fields (importance reason, "entire deal depends", and seller-entered impact). The
 * legacy CustomerImpactDto has a single free-text `customerImpactDescription`
 * field, so the richer new-flow inputs are folded into it.
 */
const ENTIRE_DEAL_TEXT = 'Entire deal depends on this';
const SAME_ISSUE_TEXT = 'Same issue as the ticket';
const WHY_IMPORTANT_PREFIX = 'Why important: ';
const OPPORTUNITY_PREFIX = 'Opportunity: ';
const STRUCTURED_VALUE_VERSION = 'v2';

export interface IParsedImpactDescription extends IImpactDescriptionFields {
    importanceReason?: ImportanceReason;
}

type ISelectedCustomerImpactInput = Omit<
    ISelectedCustomerImpact,
    'estimatedRevenue' | 'estimatedDevices' | 'revenueAtRisk' | 'devicesAffected'
> & {
    estimatedRevenue?: number | string;
    estimatedDevices?: number | string;
    revenueAtRisk?: number | string;
    devicesAffected?: number | string;
};

type ICustomerImpactMappingData = Omit<ICustomerImpactData, 'selectedImpacts'> & {
    selectedImpacts?: ISelectedCustomerImpactInput[];
};

export function buildImpactDescription(impact: IImpactDescriptionFields): string {
    const parts: string[] = [];

    if (impact.importanceReason) {
        if (impact.importanceReason === OTHER_OPTION_KEY) {
            const reasonText = impact.importanceOtherText || 'Other';
            parts.push(`${WHY_IMPORTANT_PREFIX}${encodeStructuredValue(reasonText)}`);
        } else {
            const reasonLabel =
                IMPORTANCE_REASON_LABELS[impact.importanceReason as ImportanceReason]?.label || impact.importanceReason;
            parts.push(`${WHY_IMPORTANT_PREFIX}${reasonLabel}`);
        }
    }

    if (impact.entireDealDepends) {
        parts.push(ENTIRE_DEAL_TEXT);
    }

    if (impact.sameIssue) {
        parts.push(SAME_ISSUE_TEXT);
    }

    if (impact.customerImpact && impact.customerImpact !== impact.legacyOpportunityTail) {
        parts.push(impact.customerImpact);
    }

    return parts.join('. ');
}

export function parseImpactDescription(description?: string): IParsedImpactDescription {
    const parsed: IParsedImpactDescription = {};
    let remaining = description?.trim() ?? '';
    let hasOpaqueLegacyOpportunityMarker = false;

    if (remaining.startsWith(WHY_IMPORTANT_PREFIX)) {
        remaining = remaining.slice(WHY_IMPORTANT_PREFIX.length);
        const encodedReason = parseStructuredValue(remaining);

        if (encodedReason) {
            parsed.importanceReason = OTHER_OPTION_KEY;
            parsed.importanceOtherText = encodedReason.value;
            remaining = encodedReason.remaining;
        } else {
            const knownReason = (Object.keys(IMPORTANCE_REASON_LABELS) as ImportanceReason[]).find((key) => {
                if (key === OTHER_OPTION_KEY) {
                    return false;
                }
                const label = IMPORTANCE_REASON_LABELS[key].label;
                return remaining === label || remaining.startsWith(`${label}. `);
            });

            if (knownReason) {
                parsed.importanceReason = knownReason;
                remaining = remaining.slice(IMPORTANCE_REASON_LABELS[knownReason].label.length);
            } else {
                const markerIndexes = [ENTIRE_DEAL_TEXT, OPPORTUNITY_PREFIX, SAME_ISSUE_TEXT]
                    .map((marker) => remaining.indexOf(`. ${marker}`))
                    .filter((index) => index >= 0);
                const reasonEnd = markerIndexes.length > 0 ? Math.min(...markerIndexes) : remaining.length;
                parsed.importanceReason = OTHER_OPTION_KEY;
                parsed.importanceOtherText = remaining.slice(0, reasonEnd).trim();
                remaining = remaining.slice(reasonEnd);
            }
        }
        remaining = remaining.replace(/^\.\s*/, '');
    }

    if (remaining === ENTIRE_DEAL_TEXT || remaining.startsWith(`${ENTIRE_DEAL_TEXT}. `)) {
        parsed.entireDealDepends = true;
        remaining = remaining.slice(ENTIRE_DEAL_TEXT.length).replace(/^\.\s*/, '');
    }
    if (remaining.startsWith(OPPORTUNITY_PREFIX)) {
        remaining = remaining.slice(OPPORTUNITY_PREFIX.length);
        const encodedTopic = parseStructuredValue(remaining, true);

        if (encodedTopic) {
            if (encodedTopic.value) {
                parsed.topic = encodedTopic.value;
            } else {
                hasOpaqueLegacyOpportunityMarker = true;
            }
            remaining = encodedTopic.remaining.replace(/^\.\s*/, '');
        } else {
            const sameIssueIndex = remaining.indexOf(`. ${SAME_ISSUE_TEXT}`);
            if (sameIssueIndex >= 0) {
                parsed.topic = remaining.slice(0, sameIssueIndex).trim();
                remaining = remaining.slice(sameIssueIndex).replace(/^\.\s*/, '');
            } else {
                // Legacy opportunity metadata had no reliable boundary from seller text.
                // Keep it only for edit compatibility; do not render it as impact description.
                parsed.legacyOpportunityTail = remaining.trim();
                remaining = '';
            }
        }
    }
    if (remaining === SAME_ISSUE_TEXT || remaining.startsWith(`${SAME_ISSUE_TEXT}. `)) {
        parsed.sameIssue = true;
        remaining = remaining.slice(SAME_ISSUE_TEXT.length).replace(/^\.\s*/, '');
    }

    if (!parsed.legacyOpportunityTail) {
        parsed.customerImpact = remaining;
        if (hasOpaqueLegacyOpportunityMarker && remaining) parsed.legacyOpportunityTail = remaining;
    }
    return parsed;
}

function encodeStructuredValue(value: string): string {
    return `${STRUCTURED_VALUE_VERSION}[${value.length}]${value}`;
}

function parseStructuredValue(value: string, allowPreviousFormat = false): { value: string; remaining: string } | undefined {
    const versionedPrefix = value.match(new RegExp(`^${STRUCTURED_VALUE_VERSION}\\[(\\d+)\\]`));
    const previousPrefix = allowPreviousFormat ? value.match(/^\[(\d+)\]/) : null;
    const prefix = versionedPrefix || previousPrefix;
    if (!prefix) {
        return undefined;
    }

    const valueLength = Number(prefix[1]);
    const valueStart = prefix[0].length;
    const valueEnd = valueStart + valueLength;
    const remaining = value.slice(valueEnd);

    if (valueEnd > value.length || (remaining !== '' && !remaining.startsWith('. '))) {
        return undefined;
    }

    return {
        value: value.slice(valueStart, valueEnd),
        remaining,
    };
}

/**
 * Maps the new flow's `customerImpactData.selectedImpacts` (ISelectedCustomerImpact[])
 * onto the shared backend `CustomerImpactDto[]`. Shared by the Create-Ticket and
 * Add-Customer flows so both produce identical customer-impact payloads.
 *
 * The "General feedback" tab has no per-customer impacts and yields an empty list.
 */
export function mapSelectedImpactsToDtos(customerImpactData?: ICustomerImpactMappingData | null): CustomerImpactDto[] {
    if (!customerImpactData || customerImpactData.activeTab === CUSTOMER_IMPACT_TABS.GENERAL_FEEDBACK) {
        return [];
    }

    const selectedImpacts = customerImpactData.selectedImpacts || [];
    if (selectedImpacts.length === 0) {
        return [];
    }

    return selectedImpacts.map(
        (impact) =>
            new CustomerImpactDto({
                customerName: impact.accountName || '',
                opportunityId: impact.opportunityId || '',
                msxOpportunityId: impact.msxOpportunityId || '',
                customerCountry: new CountryDto({ name: impact.country || '' }),
                customerImpactDescription: buildImpactDescription(impact),
                revenue: toDecimal(impact.revenueAtRisk),
                deviceCount: toInteger(impact.devicesAffected),
                msxRevenue: toDecimal(impact.estimatedRevenue),
                msxDeviceCount: toInteger(impact.estimatedDevices),
            }),
    );
}

/**
 * Coerces a possibly-formatted string/number (e.g. "1,000") into an integer.
 * Returns undefined for empty/non-numeric input so the field is omitted rather
 * than sent as a string the backend can't convert to Int32.
 */
function toInteger(value: unknown): number | undefined {
    if (value === null || value === undefined || value === '') return undefined;
    const n = typeof value === 'number' ? value : parseInt(String(value).replace(/[^0-9-]/g, ''), 10);
    return Number.isFinite(n) ? Math.trunc(n) : undefined;
}

/** Coerces a possibly-formatted string/number (e.g. "$1,000.50") into a number, or undefined. */
function toDecimal(value: unknown): number | undefined {
    if (value === null || value === undefined || value === '') return undefined;
    const n = typeof value === 'number' ? value : parseFloat(String(value).replace(/[^0-9.-]/g, ''));
    return Number.isFinite(n) ? n : undefined;
}
